import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import {
  Lead,
  Replacements,
  LeadMessageTemplate,
  AgentLogged,
  QuickAnswersGroups,
  QuickAnswers,
} from '@interfaces';
import { AnswersService, LeadService } from '@services';
import { replaceText } from '@utils';
import { PalleteColor } from 'nanaykuna-ui-kit/loader';
import {
  ACCEPTED_IMAGE_TYPES,
  MAX_FILES,
  ERROR_MESSAGES,
} from './multimedia-field.constants';
import type { ImageMimeType } from './multimedia-field.types';

@Component({
  selector: 'unicon-multimedia-field',
  templateUrl: './multimedia-field.component.html',
  styleUrl: './multimedia-field.component.scss',
})
export class MultimediaFieldComponent {
  @Input() lead?: Lead;
  @Input() fullname?: string;
  @Input() isBlockedByAgent: boolean = false;
  @Input() agent: string = 'otro agente';
  @Input() isClosedLead: boolean = false;
  @Input() leadId: string = '';
  @Input() sellerName?: AgentLogged;
  @Input() isActivated: boolean = true;
  @Input() defaultQuickGroupId!: number;
  @Output() message: EventEmitter<FormData> = new EventEmitter<FormData>();
  @ViewChild('messageInput') input!: ElementRef<HTMLTextAreaElement>;

  protected chatForm!: FormGroup;
  protected files: File[] = [];
  protected audioFile: File | null = null;

  protected visibleMenu: boolean = false;
  protected visibleOptions: boolean = false;
  protected visibleAudioRecording: boolean = false;
  protected isRecodingAudio: boolean = false;
  protected options: LeadMessageTemplate[] = [];
  protected selectedOption!: LeadMessageTemplate;
  protected isLoading: boolean = false;
  protected show: boolean = false;
  protected quickAnswersList: QuickAnswersGroups[] = [];
  protected quickAnswerType: QuickAnswers[] = [];
  protected selectedQuickAnswerType!: number;
  protected isLoadingQuickAnswers: boolean = false;
  protected selectAnswerType: QuickAnswers = {} as QuickAnswers;

  constructor(
    private formBuilder: FormBuilder,
    private leadService: LeadService,
    private quickAnswersService: AnswersService,
  ) {
    this.setChatForm();
  }

  ngOnInit(): void {
    this.getQuickAnswersGroups();
  }

  private getQuickAnswersGroups(): void {
    this.quickAnswersService.getQuickAnswers().subscribe({
      next: (quickAnswerType: QuickAnswers[]) => {
        this.quickAnswerType = quickAnswerType.map(item => ({
          ...item,
          name: item.description,
          description: item.name,
          active: false,
        }));
        this.selectedQuickAnswerType = this.defaultQuickGroupId;
        const defaultType = this.quickAnswerType.find(
          item => item.id === this.defaultQuickGroupId,
        );
        if (defaultType) {
          this.getQuickAnswersByGroup(defaultType);
        }
      },
    });
  }

  private getQuickAnswersByGroup(type: QuickAnswers): void {
    this.isLoadingQuickAnswers = true;
    if (type && type.id) {
      this.quickAnswersService.getQuickAnswerById(type.id).subscribe({
        next: (quickAnswers: QuickAnswersGroups[]) => {
          this.quickAnswersList = quickAnswers.map(item => ({
            ...item,
            isActivated: false,
          }));
          this.isLoadingQuickAnswers = false;
        },
        error: () => {
          this.isLoadingQuickAnswers = false;
        },
      });
    }
  }

  protected setActiveAAnswerType(type: number) {
    this.selectedQuickAnswerType = type;
    this.selectAnswerType =
      this.quickAnswerType.find(item => item.id === type) ||
      this.quickAnswerType[0];
    this.getQuickAnswersByGroup(this.selectAnswerType);
  }

  protected onInitAudioRecording() {
    this.isRecodingAudio = true;
  }

  protected onAudioPermissionError(error: Error) {
    setTimeout(() => {
      this.visibleAudioRecording = false;
      alert(error.message);
    }, 100);
  }

  protected onAudioRecorded(audioFile: File) {
    this.audioFile = audioFile;
  }

  protected initAudioRecord() {
    this.visibleAudioRecording = true;
  }

  protected onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selectedFiles = input.files;

    if (selectedFiles && selectedFiles.length > MAX_FILES) {
      alert(ERROR_MESSAGES.MAX_FILES);
      input.value = '';
      return;
    }

    if (selectedFiles) {
      this.files = Array.from(selectedFiles);
      this.input.nativeElement.focus();
    }
  }

  protected onDeleteFile(fileName: string): void {
    this.files = this.files.filter(file => file.name !== fileName);
  }

  protected onSubmit(): void {
    const formData = new FormData();
    const textMessage: string = this.chatForm.get('textMessage')?.value || '';
    let haveFormDataContent = false;

    if (textMessage) {
      haveFormDataContent = true;
      formData.append('text', textMessage);
    }

    if (this.files.length > 0) {
      haveFormDataContent = true;
      this.files.forEach(file => {
        formData.append('files', file);
      });
    }

    if (this.audioFile != null) {
      haveFormDataContent = true;
      formData.append('files', this.audioFile);
    }

    if (haveFormDataContent) {
      this.message.emit(formData);
      this.files = [];
      this.chatForm.reset();
      this.deleteAudioRecording();
    }
  }

  protected getFillColor(sendButton = false): PalleteColor {
    if (this.isBlockedByAgent || this.isClosedLead) {
      return 'neutrals-500';
    }

    if (!sendButton) {
      return 'neutrals-600';
    }

    return this.canSendMessage ? 'primary-700' : 'primary-200';
  }

  protected onReactivateChat(): void {
    this.visibleMenu = true;
    this.visibleOptions = true;
    this.leadService.getMessageTemplate(this.leadId).subscribe({
      next: templates => {
        this.options = templates;
        this.visibleOptions = false;
      },
      error: () => {
        this.visibleOptions = false;
      },
    });
  }

  protected onCloseTemplateList(): void {
    this.visibleMenu = false;
  }

  protected selectedTemplate(): void {
    this.isLoading = true;
    const request = {
      templateName: this.selectedOption.name,
    };
    this.leadService.sendMessageTemplate(request, this.leadId).subscribe({
      next: () => {
        this.isLoading = false;
        this.visibleMenu = false;
      },
      error: () => {
        this.isLoading = false;
        this.visibleMenu = false;
      },
    });
  }

  protected addQuickAnswerOnChat(answer: QuickAnswersGroups): void {
    if (this.sellerName) {
      const replacements = {
        [Replacements.USER_NAME]: `${this.fullname}`,
        [Replacements.SELLER_NAME]: `${this.sellerName.firstName} ${this.sellerName.lastName}`,
        [Replacements.PRODUCT]: `Concreto`,
      };

      const replacedText = replaceText(answer.message, replacements);
      this.chatForm = this.formBuilder.group({
        textMessage: new FormControl(replacedText),
      });

      setTimeout(() => this.autoResize(this.input.nativeElement), 100);
    }
  }

  protected deleteAudioRecording() {
    this.audioFile = null;
    this.isRecodingAudio = false;
    this.visibleAudioRecording = false;
    setTimeout(() => this.autoResize(this.input.nativeElement), 100);
  }

  protected autoResize(textarea: HTMLTextAreaElement): void {
    textarea.style.height = '20px';
    const scrollHeight = textarea.scrollHeight;
    const maxHeight = 72;

    if (scrollHeight <= maxHeight) {
      textarea.style.height = `${scrollHeight}px`;
      textarea.style.overflowY = 'hidden';
      return;
    }

    textarea.style.height = `${maxHeight}px`;
    textarea.style.overflowY = 'auto';
    textarea.scrollTop = textarea.scrollHeight;
  }

  @HostListener('keydown', ['$event'])
  protected handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      if (event.shiftKey) {
        return;
      }

      event.preventDefault();

      this.onSubmit();
    }
  }

  @HostListener('paste', ['$event'])
  protected handlePaste(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;

    const items = Array.from(clipboardData.items);
    const imageItems = items.filter(item => item.type.startsWith('image/'));

    if (imageItems.length === 0) return;

    event.preventDefault();
    const pastedFiles = this.extractImagesFromClipboard(imageItems);

    if (pastedFiles.length > 0) {
      this.addPastedImages(pastedFiles);
    }
  }

  private extractImagesFromClipboard(items: DataTransferItem[]): File[] {
    const files: File[] = [];

    for (const item of items) {
      const file = item.getAsFile();

      if (!file) continue;

      if (this.isValidImageType(file.type as ImageMimeType)) {
        const renamedFile = this.createPastedImageFile(file);
        files.push(renamedFile);
        continue;
      }

      alert(ERROR_MESSAGES.UNSUPPORTED_FORMAT);
    }

    return files;
  }

  private isValidImageType(type: string): boolean {
    return ACCEPTED_IMAGE_TYPES.includes(type as ImageMimeType);
  }

  private createPastedImageFile(blob: Blob): File {
    const timestamp = Date.now();
    const extension = blob.type.split('/')[1];
    const fileName = `pasted-image-${timestamp}.${extension}`;

    return new File([blob], fileName, { type: blob.type });
  }

  private addPastedImages(pastedFiles: File[]): void {
    const availableSlots = MAX_FILES - this.files.length;

    if (availableSlots <= 0) {
      alert(ERROR_MESSAGES.MAX_FILES);
      return;
    }

    const filesToAdd = pastedFiles.slice(0, availableSlots);
    if (pastedFiles.length > availableSlots) {
      alert(ERROR_MESSAGES.MAX_FILES);
    }

    this.files = [...this.files, ...filesToAdd];

    setTimeout(() => {
      if (this.input?.nativeElement) {
        this.input.nativeElement.focus();
        this.autoResize(this.input.nativeElement);
      }
    }, 0);
  }

  get canSendMessage(): boolean {
    const textMessage = this.chatForm.get('textMessage')?.value || '';
    const haveContentToSend =
      textMessage.trim() !== '' ||
      this.files.length > 0 ||
      this.audioFile != null;
    return haveContentToSend && !this.isClosedLead && !this.isBlockedByAgent;
  }

  private setChatForm(): void {
    this.chatForm = this.formBuilder.group({
      textMessage: new FormControl(''),
    });
  }
}
